import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

const LoadingContext = createContext({
  isLoading: false,
  activeRequests: 0,
});

export const useLoading = () => useContext(LoadingContext);

export const LoadingProvider = ({ children }) => {
  const [activeRequests, setActiveRequests] = useState(0);
  const counterRef = useRef(0);

  const increment = useCallback(() => {
    counterRef.current += 1;
    setActiveRequests(counterRef.current);
  }, []);

  const decrement = useCallback(() => {
    counterRef.current = Math.max(0, counterRef.current - 1);
    setActiveRequests(counterRef.current);
  }, []);

  useEffect(() => {
    // Add request interceptor
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        increment();
        return config;
      },
      (error) => {
        decrement();
        return Promise.reject(error);
      }
    );

    // Add response interceptor
    const responseInterceptor = api.interceptors.response.use(
      (response) => {
        decrement();
        return response;
      },
      (error) => {
        decrement();
        return Promise.reject(error);
      }
    );

    // Cleanup interceptors on unmount
    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, [increment, decrement]);

  const isLoading = activeRequests > 0;

  return (
    <LoadingContext.Provider value={{ isLoading, activeRequests }}>
      {children}
    </LoadingContext.Provider>
  );
};

export default LoadingContext;
